import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isWithinLimit } from '@inference.engine/file-utils/web';
import { useChatStoreInput, useCurrentAgentId } from '@/features/chats/stores';
import { notificationService } from '@/lib/services/notification.service';
import { logger } from '@/lib/utils/logger';
import { FILE_VALIDATION_MESSAGES } from '@/shared/constants/file-upload.constants';
import { i18n } from '@/shared/i18n';
import { getFileUploadUrl, uploadFileToS3 } from '@/shared/lib/file-upload';
import {
  convertToPdf,
  FileCancellationError,
  getFileDownloadUrl,
  pollConversionUntilDone,
} from '../../api/chats.api';
import { nextAttachmentOrder } from '../../lib/attachmentOrder';
import { FileAssetWithUrl } from '../../model/chats.types';
import { isConvertibleFile, shouldBypassFileConversion } from '../../model/file-upload.constants';
import {
  FileInputWithStatus,
  FileStatus,
  type FileStatusType,
  type UseFileAttachmentsProps,
  type UseFileAttachmentsReturn,
} from './useFileAttachments.types';

export const useFileAttachments = ({
  getModelFromContext,
}: UseFileAttachmentsProps): UseFileAttachmentsReturn => {
  const { t } = useTranslation('chats');
  const { chatInput, setFiles } = useChatStoreInput();
  const currentAgentId = useCurrentAgentId();
  const [isUploading, setIsUploading] = useState(false);
  const uploadControllersRef = useRef<Map<string, AbortController>>(new Map());

  useEffect(() => {
    const controllers = uploadControllersRef.current;
    return () => {
      // Abort all ongoing uploads when component unmounts
      controllers.forEach((controller) => {
        controller.abort();
      });
      controllers.clear();
    };
  }, []);

  const handleRemoveFile = useCallback(
    (assetIdToRemove: string) => {
      // Get the abort controller for this upload
      const controller = uploadControllersRef.current.get(assetIdToRemove);

      // Abort the upload if it's in progress
      if (controller) {
        controller.abort();
        uploadControllersRef.current.delete(assetIdToRemove);
      }

      setFiles((prev) => prev.filter((file) => file.assetId !== assetIdToRemove));
    },
    [setFiles],
  );

  const clearFiles = useCallback(() => {
    setFiles([]);
  }, [setFiles]);

  const addFile = useCallback(
    (file: FileInputWithStatus) => {
      setFiles((prev) => {
        if (prev.some((f) => f.assetId === file.assetId)) return prev;
        return [...prev, file];
      });
    },
    [setFiles],
  );

  // Validate a single file and return validation result
  const validateFiles = useCallback(
    (files: File[]): { isValid: boolean; error?: string } => {
      const agentModel = getModelFromContext?.();

      // mimeType is required for isWithinLimit to apply the higher video limit
      // (100MB) instead of the default 20MB LLM cap.
      const filesToCheck = files.map((f) => ({
        mimeType: f.type,
        metadata: {
          fileName: f.name,
          size: f.size,
        },
      }));

      chatInput.files.forEach((f) => {
        filesToCheck.push({
          mimeType: f.mimeType,
          metadata: {
            fileName: f.metadata.fileName,
            size: f.metadata.size ?? 0,
          },
        });
      });

      const validation = isWithinLimit(filesToCheck, agentModel);

      if (!validation.isValid) {
        const errorMsg = FILE_VALIDATION_MESSAGES.SIZE_EXCEEDED(
          i18n.t('common:constants.fileUpload.files'),
          Math.floor(validation.maxAllowed / (1024 * 1024)),
        );
        return { isValid: false, error: errorMsg };
      }

      return { isValid: true };
    },
    [getModelFromContext, chatInput.files],
  );

  // Generate a unique temporary ID for uploading files
  const generateTempId = useCallback((): string => {
    return `temp-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }, []);

  // Add multiple files with UPLOADING status, filtering out invalid files
  const setFilesWithStatusLoading = useCallback(
    (files: File[]): { files: FileInputWithStatus[]; error?: string } => {
      const validFiles: FileInputWithStatus[] = [];

      const validation = validateFiles(files);

      if (!validation.isValid) {
        return { files: [], error: validation.error };
      }

      files.forEach((file) => {
        const assetId = generateTempId();
        const abortController = new AbortController();

        const uploadingFile: FileInputWithStatus = {
          assetId,
          status: FileStatus.UPLOADING,
          file,
          mimeType: file.type,
          url: '',
          metadata: {
            fileName: file.name,
            size: file.size,
          },
          abortController,
          addedAt: nextAttachmentOrder(),
        };

        uploadControllersRef.current.set(assetId, abortController);
        addFile(uploadingFile);
        validFiles.push(uploadingFile);
      });

      return { files: validFiles };
    },
    [validateFiles, generateTempId, addFile],
  );

  // Update file status by filename and current status
  const updateFileByName = useCallback(
    (assetId: string, currentStatus: FileStatusType, updates: Partial<FileInputWithStatus>) => {
      setFiles((prev) => {
        return prev.map((f) =>
          f.assetId === assetId && f.status === currentStatus ? { ...f, ...updates } : f,
        );
      });
    },
    [setFiles],
  );

  // Create FormData for S3 upload
  const createFormData = useCallback((fields: Record<string, string>, file: File): FormData => {
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value);
    });
    formData.append('file', file);
    return formData;
  }, []);

  // Replace an uploading file with its final uploaded version
  const replaceUploadingFile = useCallback(
    (tempAssetId: string, uploadedFile: FileInputWithStatus) => {
      setFiles((prevFiles) => {
        if (prevFiles.length === 0) return [uploadedFile];

        const updatedFiles = prevFiles.map((existingFile) =>
          existingFile.assetId === tempAssetId && existingFile.status === FileStatus.UPLOADING
            ? // Keep the uploading entry's insertion order so completing an upload
              // doesn't reshuffle the strip.
              { ...uploadedFile, addedAt: existingFile.addedAt }
            : existingFile,
        );

        const wasReplaced = !updatedFiles.some(
          (f) => f.assetId === tempAssetId && f.status === FileStatus.UPLOADING,
        );
        if (!wasReplaced) {
          logger.warn(
            `Could not find uploading file "${uploadedFile.file.name}" to replace. Adding as new file.`,
          );
          return [...prevFiles, uploadedFile];
        }

        uploadControllersRef.current.delete(tempAssetId);
        return updatedFiles;
      });
    },
    [setFiles],
  );

  // Handle file upload failure
  const handleUploadError = useCallback(
    (err: unknown, tempAssetId: string, fileName: string): undefined => {
      // Check for cancellation errors (Axios CanceledError or our custom FileCancellationError)
      const isCancelled =
        err instanceof FileCancellationError ||
        (err instanceof Error && err.name === 'CanceledError');

      if (isCancelled) {
        logger.debug(`Upload cancelled for file "${fileName}"`);
        setFiles((prevFiles) => prevFiles.filter((f) => f.assetId !== tempAssetId));
        uploadControllersRef.current.delete(tempAssetId);
        return undefined;
      }

      const reason =
        err instanceof Error ? err.message : t('chats.useFileAttachments.unknownError');
      const errorMsg = t('chats.useFileAttachments.failedToUploadFile', { fileName, reason });
      logger.error(errorMsg, err);
      notificationService.showError(errorMsg);

      updateFileByName(tempAssetId, FileStatus.UPLOADING, { status: FileStatus.FAILED });
      return undefined;
    },
    [updateFileByName, setFiles, t],
  );

  // Upload a file that needs conversion to PDF (docx/pptx/xlsx)
  const handleConversionUpload = useCallback(
    async ({
      file,
      assetId: tempAssetId,
    }: FileInputWithStatus): Promise<FileInputWithStatus | undefined> => {
      try {
        notificationService.showInfo(t('chats.useFileAttachments.convertingFileType'));

        const controller = uploadControllersRef.current.get(tempAssetId);
        const jobId = await convertToPdf(file, controller?.signal);
        const { assetId, pdfSize } = await pollConversionUntilDone(jobId, controller?.signal);
        const downloadUrl = await getFileDownloadUrl(assetId);

        const pdfFilename = file.name.replace(/\.[^.]+$/, '.pdf');
        // Use PDF size if available, otherwise fall back to 0 (empty File blob)
        const fileSize = pdfSize ?? 0;

        const uploadedFile: FileInputWithStatus = {
          assetId,
          status: FileStatus.UPLOADED,
          file: new File([], pdfFilename, { type: 'application/pdf' }),
          mimeType: 'application/pdf',
          url: downloadUrl,
          metadata: {
            fileName: pdfFilename,
            size: fileSize,
          },
        };

        replaceUploadingFile(tempAssetId, uploadedFile);
        return uploadedFile;
      } catch (err) {
        return handleUploadError(err, tempAssetId, file.name);
      }
    },
    [replaceUploadingFile, handleUploadError, t],
  );

  // Single file upload function (presigned URL → S3)
  const handleSingleFileUpload = useCallback(
    async ({
      file,
      assetId: tempAssetId,
    }: FileInputWithStatus): Promise<FileInputWithStatus | undefined> => {
      try {
        const { url, fields, assetId } = await getFileUploadUrl(file.type, file.name);
        const downloadUrl = await getFileDownloadUrl(assetId);
        const formData = createFormData(fields, file);
        const controller = uploadControllersRef.current.get(tempAssetId);

        await uploadFileToS3(url, formData, controller?.signal);

        const uploadedFile: FileInputWithStatus = {
          assetId,
          status: FileStatus.UPLOADED,
          file,
          mimeType: file.type,
          url: downloadUrl,
          metadata: {
            fileName: file.name,
            size: file.size,
          },
        };

        replaceUploadingFile(tempAssetId, uploadedFile);
        return uploadedFile;
      } catch (err) {
        return handleUploadError(err, tempAssetId, file.name);
      }
    },
    [createFormData, replaceUploadingFile, handleUploadError],
  );

  const handleFileUpload = useCallback(
    async (files: File[]): Promise<void> => {
      setIsUploading(true);

      // Add valid files to the UI with loading status
      const { files: validFiles, error: validationError } = setFilesWithStatusLoading(files);

      if (validFiles.length === 0) {
        setIsUploading(false);
        if (validationError) {
          notificationService.showError(validationError);
        }
        return;
      }

      // Upload valid files sequentially, routing convertible types through PDF conversion
      // unless the current agent processes that specific mimetype natively
      // (e.g. translation agent handles .xlsx with its own parser).
      for (const fileItem of validFiles) {
        try {
          const shouldConvert =
            isConvertibleFile(fileItem.mimeType) &&
            !shouldBypassFileConversion(currentAgentId, fileItem.mimeType);
          if (shouldConvert) {
            await handleConversionUpload(fileItem);
          } else {
            await handleSingleFileUpload(fileItem);
          }
        } catch (error) {
          logger.error(`Failed to upload ${fileItem.assetId}:`, error);
        }
      }

      setIsUploading(false);
    },
    [setFilesWithStatusLoading, handleSingleFileUpload, handleConversionUpload, currentAgentId],
  );

  // Multiple files upload handler - uploads one by one
  const handleInputFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
      const fileList = event.target.files;
      if (!fileList || fileList.length === 0) return;
      await handleFileUpload(Array.from(fileList));
      event.target.value = '';
    },
    [handleFileUpload],
  );

  const handleFileReference = useCallback(
    (file: FileAssetWithUrl) => {
      setFiles((prevFiles) => {
        const exists = prevFiles.some((f) => f.assetId === file.assetId);
        if (exists) return prevFiles;

        return [
          ...prevFiles,
          {
            assetId: file.assetId,
            status: FileStatus.REFERENCED,
            file: new File([], file.metadata.fileName, { type: file.mimeType }),
            mimeType: file.mimeType,
            url: file.url,
            metadata: file.metadata,
            addedAt: nextAttachmentOrder(),
          },
        ];
      });
    },
    [setFiles],
  );

  return {
    files: chatInput.files,
    isUploading,
    handleFileUpload,
    handleInputFileUpload,
    handleFileReference,
    handleRemoveFile,
    clearFiles,
  };
};
